<template>
    <NuxtLayout name="catalog">
        <div class="group">
            <div class="page-container">
                <MoleculesGlobalBreadcrumb class="my-8" :segments="entity.parents"></MoleculesGlobalBreadcrumb>
                <div class="flex flex-wrap lg:gap-8 lg:flex-nowrap">
                    <div class="flex-1 order-2 min-w-0">
                        <h1 class="mb-6 text-4xl font-secondary">{{ name }}</h1>

                        <!-- description -->
                        <div class="group__description" :class="{ 'group__description--collapsed': description.length > 500 && descriptionCollapsed }" v-html="description" v-if="description"></div>
                        <button v-if="description.length > 500" class="text-conversion text-sm underline" @click="descriptionCollapsed = !descriptionCollapsed" v-html="descriptionCollapsed ? $t('Zobrazit více') : $t('Zobrazit méně')"></button>

                        <!-- subgroups -->
                        <div v-if="subGroups?.length && (totalItems > 0 || route.hash.length)" class="grid grid-cols-1  xl:grid-cols-3 gap-6 mt-12">
                            <MoleculesBlocksSubGroupItem v-for="item in subGroups" :id="item.id" :name="item.name" :full-name="item.page.title" :url="item.page.url" />
                        </div>

                        <!-- bestsellers -->
                        <div class="group__bestsellers" v-if="entity.wareCount >= 14 && bestsellers && bestsellers.length >= 5">
                            <h2 class="mt-12 mb-6 text-2xl font-secondary">{{ $t('Nejprodávanější v kategorii') }}</h2>
                            <OrganismsListsWareList :items="bestsellers" :with-sorting="false" type="slider" :list-name="`Bestsellery - ${usePage().value.page.title}`" :group-id="entity.id" />
                        </div>

                        <!-- filter button -->
                        <div class="block mt-6 p-4 lg:hidden text-center uppercase bg-secondary cursor-pointer" @click="filterVisible = true">
                            <AtomsImagesIcon icon="filtrovani" class="mr-4" />{{ $t('Zobrazit filtr produktů') }}
                        </div>

                        <!-- ware items -->
                        <div v-if="totalItems > 0">
                            <OrganismsListsWareList :items="items" :with-sorting="true" :page-size="appConfig.wareList.itemsOnPage" :page="page" :total-items="totalItems" :list-name="`Kategorie - ${usePage().value.page.title}`" :group-id="entity.id" :filter="getFilterForAnalytics()" />
                        </div>

                        <!-- empty filter results -->
                        <template v-else-if="route.hash.length">
                            <div class="my-10 text-xl text-gray">{{ $t('Podle zadaného filtru nebyly nalezeny žádné produkty.') }}</div>
                            <TemplatesNotFound />
                        </template>

                        <!-- empty group -->
                        <template v-else>
                            <div class="my-10 text-xl text-gray">{{ $t('Z této kategorie nenabízíme žádné zboží.') }}</div>
                            <TemplatesNotFound />
                        </template>
                    </div>
                    <div v-if="totalItems > 0 || route.hash.length">
                        <div class="group__filter" :class="{ 'group__filter--visible': filterVisible }">
                            <!-- filter header -->
                            <div class="flex justify-between lg:hidden">
                                <div class="flex-1 uppercase text-center font-bold">{{ $t('Filtr produktů') }}</div>
                                <AtomsImagesIcon class="cursor-pointer" icon="zavreni" @click="filterVisible = false" />
                            </div>

                            <!-- filter boxes -->
                            <MoleculesBlocksFilterItem @apply-filter="filterApplied" v-for="item in filter" :label="item.label" :url-parameter="item.urlParameter" :range="item.range" :min-value="item.minValue" :max-value="item.maxValue" :min-range="item.minRange" :max-range="item.maxRange" :options="item.options" />

                            <!-- filter footer -->
                            <div class="flex gap-6 justify-center lg:hidden">
                                <MoleculesButtonsButton @click="filterVisible = false">Zobrazit produkty</MoleculesButtonsButton>
                                <button @click="useRouter().push(''); filterVisible = false">Zrušit filtr</button>
                            </div>
                        </div>
                    </div>
                </div>
                <TemplatesRecentlyViewedItems />
            </div>
        </div>
    </NuxtLayout>
</template>
<script setup>

import "assets/css/slider.css";
import "assets/css/carousel.css";

const appConfig = useAppConfig();
const entity = useEntity();
const route = useRoute();
const utils = useUtils();
const i18n = useI18n();
const shopRouting = useShopRouting();
const locale = useLocale();

const name = entity.name;
const description = entity.annotation ?? "";
const subGroups = entity.groups;

const hash = ref(shopRouting.parseHash());

const items = ref(entity.wares.wares.items);
const bestsellers = entity.bestsellers.wares.items;
const page = ref(1);
const totalItems = ref(entity.wareCount);
const descriptionCollapsed = ref(true);
const filterVisible = ref(false);
const filter = ref([]);

const buildBasicFilter = (minRange, maxRange) => {
    return [
        {
            label: 'Dostupnost',
            urlParameter: 'availability',
            options: [
                {
                    text: i18n.t('Skladem'),
                    urlValue: 'in-stock',
                },
                {
                    text: i18n.t('Doprodej'),
                    urlParameter: appConfig.basicParameterFilter.discount.urlParameter,
                    urlValue: appConfig.basicParameterFilter.discount.urlValue
                }
            ]
        },
        {
            label: i18n.t('Cena'),
            urlParameter: 'price',
            range: 'price',
            minValue: minRange,
            maxValue: maxRange,
            minRange: minRange,
            maxRange: maxRange
        }
    ];
}

const buildParametricFilter = (properties) => {
    return properties
        // remove items without valid options
        .filter((item) => !!item.values.find(q => q.value))
        .map((item) => {

            const urlParameter = `${item.id}:${utils.slugify(item.name)}`

            const result = {
                label: item.name,
                urlParameter,
                options: item.values
                    // remove options without valid values
                    .filter((option) => {
                        return !!option.value;
                    })
                    .map((option) => {

                        const urlValue = `${option.lValue}:${utils.slugify(option.value)}`;

                        return {
                            text: option.value,
                            lValue: option.lValue,
                            urlValue,
                        }
                    })
                    .sort((a, b) => {

                        const clearA = a.text.trim().toLowerCase();
                        const clearB = b.text.trim().toLowerCase();

                        if (clearA < clearB) {
                            return -1;
                        }
                        else if (clearA > clearB) {
                            return 1;
                        }
                        else {
                            return 0;
                        }
                    })
            };

            return result;
        })
        .sort((a, b) => b.priority - a.priority);
}

const buildFilter = () => {

    const basicFilter = buildBasicFilter(entity.wares.itemsProperties.minPrice.price, entity.wares.itemsProperties.maxPrice.price);
    const parametricFilter = buildParametricFilter(entity.wares.itemsProperties.properties);

    const result = basicFilter.concat(parametricFilter);

    return result;
}

// synchronizes filter controls with parameters coming from URL/hash
const updateFilter = (availableValues) => {

    filter.value.forEach((item) => {
        if (item.options && item.options.length > 0) {
            item.options.forEach((option) => {
                option.selected = isFilterValueInHash(item.urlParameter, option.urlValue);
                option.unavailable = option.lValue && availableValues && !availableValues.includes(option.lValue);
            });
        }
        else if (item.range) {
            const selectedRange = hash.value.find(q => q.key === item.urlParameter)?.value[0];

            if (selectedRange && selectedRange.indexOf('-') > -1) {
                const rangeValues = selectedRange.split('-');

                if (rangeValues.length == 2) {
                    item.minValue = parseInt(rangeValues[0]);
                    item.maxValue = parseInt(rangeValues[1]);
                }
            }
            else {
                item.minValue = item.minRange;
                item.maxValue = item.maxRange;
            }
        }
    });
}

const isFilterValueInHash = (urlParameter, urlValue) => !!hash.value.find(q => q.key === urlParameter && q.value.find(qq => qq === urlValue));

const filterApplied = (urlParameter, urlValue, add, replace) => {
    // reset page to 1
    page.value = 1;
    const modifiedHash = shopRouting.stringifyHash(shopRouting.updateValueInHash('p', '', false, true));

    // update filtered value in hash
    const hashObj = shopRouting.updateValueInHash(urlParameter, urlValue, add, replace, modifiedHash);

    // navigate to the modified hash
    shopRouting.pushHash(hashObj);
}

filter.value = buildFilter();

// set selected items in filter controls
if (hash.value) {
    updateFilter();
}

const processSpecificFetch = async (url, query) => {
    const fn = process.server ? useApiFetch : $fetch;

    const response = await fn(url, { query: query });

    if (process.server) {
        return response;
    }
    else {
        return response.wareList;
    }
}

const getFilterForAnalytics = () => {

    const result = shopRouting.parseHash().filter(q => q.key.indexOf(':')).sort((a, b) => a.key - b.key).map(item => {
        return `${item.key.split(':')[1]}:${item.value.sort((a, b) => a.key - b.key).map(item1 => item1.split(':')[1]).join(',')}`
    }).join(',');

    return result;
}

// reload items when the query changes and also when the initial load contains some parameters for filtering, sorting or paging
watch(() => route.hash, async () => {

    hash.value = shopRouting.parseHash();

    const pageParameter = hash.value.find(q => q.key === 'p')?.value.toString() ?? "1";
    const pageNumber = parseInt(pageParameter.split('-')[0]);
    const append = pageParameter.split('-').length === 2 && pageParameter.split('-')[1] === 'a';

    page.value = pageNumber;

    const modifiedHashObj = shopRouting.updateValueInHash('idGroup', entity.id, false, true);

    const updatedData = await processSpecificFetch(`/api/wareList?idGroup=${entity.id}&currencyId=${locale.getLocale().currencyId}&languageId=${locale.getLocale().languageId}&${shopRouting.stringifyHash(modifiedHashObj).replace('#!', '')}`);

    if (append) {
        items.value = items.value.concat(updatedData.wares.items);
    }
    else {
        items.value = updatedData.wares.items;
    }

    totalItems.value = updatedData.wares.count;

    updateFilter(updatedData.itemsProperties.properties.flatMap(item => item.values.flatMap(value => value.lValue)));

}, { flush: 'pre', immediate: hash.value.length > 0, deep: false });

</script>
<style lang="postcss">
.group {
    @apply pb-6;

    &__description {
        @apply max-h-[100rem] -mt-6 mb-6 leading-6 overflow-hidden text-gray ease-in-out duration-500;

        & p {
            @apply pt-6;
        }

        &--collapsed {
            @apply max-h-[6rem];
        }
    }

    &__filter {
        @apply fixed left-0 top-0 right-0 bottom-0 z-20 w-auto space-y-6 p-6 opacity-0 ease-in-out duration-500 bg-white pointer-events-none;

        &--visible {
            @apply overflow-auto opacity-100 pointer-events-auto;
        }

        @media screen(lg) {
            @apply relative -top-4 w-80 p-0 opacity-100 pointer-events-auto;
        }
    }
}
</style>